import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  type NotificationItem,
} from "@/redux/api/notifications.rtkq";
import { cn } from "@/lib/utils";

const NotificationList = ({
  notifications,
  count,
  timeAgo,
  onMarkRead,
  onMarkAllRead,
  showTitle = true,
  headerClassName,
}: {
  notifications: NotificationItem[];
  count: number;
  timeAgo: (dateStr: string) => string;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  showTitle?: boolean;
  headerClassName?: string;
}) => (
  <>
    {(showTitle || count > 0) && (
      <div
        className={`flex items-center justify-between gap-2 border-b border-border px-3 py-2 ${headerClassName ?? ""}`}
      >
        {showTitle ? (
          <span className="text-sm font-semibold">התראות</span>
        ) : (
          <span />
        )}
        {count > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={onMarkAllRead}
          >
            סמן הכל כנקרא
          </Button>
        )}
      </div>
    )}

    <div className="min-h-0 flex-1 overflow-y-auto">
      {notifications.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          אין התראות
        </div>
      ) : (
        notifications.map((n) => (
          <div
            key={n.id}
            onClick={() => !n.isRead && onMarkRead(n.id)}
            className={`cursor-pointer border-b border-border/50 px-3 py-2.5 transition-colors hover:bg-muted/50 ${
              !n.isRead ? "bg-chen-900/5" : ""
            }`}
          >
            <div className="flex items-start gap-2">
              {!n.isRead && (
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-chen-900" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-foreground">
                  {n.title}
                </div>
                {n.body && (
                  <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                    {n.body}
                  </div>
                )}
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {timeAgo(n.createdAt)}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </>
);

type NotificationBellProps = {
  /** Where the bell is mounted. */
  placement?: "mobile" | "sidebar";
};

const NotificationBell = ({ placement = "mobile" }: NotificationBellProps) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const useMobileSheet = isMobile || placement === "mobile";
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 30_000,
  });
  const { data: notifData } = useGetNotificationsQuery(
    { limit: 20 },
    { skip: !open },
  );
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const count = unreadData?.count || 0;
  const notifications = notifData?.notifications || [];

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "עכשיו";
    if (mins < 60) return `${mins}ד'`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}ש'`;
    return `${Math.floor(hours / 24)}י'`;
  };

  const listProps = {
    notifications,
    count,
    timeAgo,
    onMarkRead: (id: string) => markRead(id),
    onMarkAllRead: () => markAllRead(),
  };

  const trigger =
    placement === "sidebar" ? (
      <SidebarMenuButton
        type="button"
        tooltip="התראות"
        onClick={() => setOpen(true)}
        className="relative"
      >
        <Bell className="h-4 w-4" />
        <span>התראות</span>
        {count > 0 && (
          <span className="ms-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </SidebarMenuButton>
    ) : (
      <Button
        variant="ghost"
        size="icon"
        className={cn("relative", useMobileSheet ? "size-11 [&_svg]:size-6" : "size-9 [&_svg]:size-5")}
        onClick={() => setOpen(true)}
        aria-label="התראות"
        title="התראות"
      >
        <Bell />
        {count > 0 && (
          <span className="absolute -top-0.5 -left-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </Button>
    );

  return (
    <div className={cn("relative", placement === "sidebar" && "w-full")} dir="rtl">
      {trigger}

      {useMobileSheet ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="flex h-[min(70dvh,32rem)] flex-col gap-0 p-0 pb-[env(safe-area-inset-bottom)]"
          >
            <SheetHeader className="border-b border-border py-3 pr-14 pl-4 text-start">
              <SheetTitle className="text-base">התראות</SheetTitle>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col">
              <NotificationList {...listProps} showTitle={false} />
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="flex max-h-[min(32rem,80vh)] w-[min(24rem,calc(100vw-2rem))] flex-col gap-0 overflow-hidden p-0"
            dir="rtl"
          >
            {/* Close uses absolute end-4; reserve that side so title never overlaps */}
            <DialogHeader className="gap-0 border-b border-border px-4 py-3 pe-12 text-start sm:text-start">
              <DialogTitle className="leading-none">התראות</DialogTitle>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <NotificationList {...listProps} showTitle={false} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default NotificationBell;
